import React from 'react';
import { X } from 'lucide-react';
import type { CheckBarcodesResult } from '../api/vlListing.api';
import styles from './CheckBarcodesModal.module.css';

type Props = {
  open: boolean;
  loading: boolean;
  error?: string | null;
  result: CheckBarcodesResult | null;
  onClose: () => void;
};

const CheckBarcodesModal: React.FC<Props> = ({ open, loading, error, result, onClose }) => {
  if (!open) return null;
  const seats = result?.seats || [];

  return (
    <div className={styles.overlay}>
      <div
        className={styles.content}
        role="dialog"
        aria-modal="true"
        aria-labelledby="check-barcodes-title"
      >
        <div className={styles.header}>
          <div>
            <h3 id="check-barcodes-title">Check Barcodes</h3>
            <p className={styles.subtitle}>
              {result?.eventName
                ? `${result.eventName} — all listed/verified seats for this event`
                : 'Shows whether Victory Live has a barcode on each seat for this event.'}
            </p>
          </div>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className={styles.body}>
          {loading && <p className={styles.muted}>Checking barcodes in Victory Live…</p>}
          {error && <p className={styles.error}>{error}</p>}
          {!loading && result && (
            <>
              <div className={styles.summary}>
                <span>{result.total} seat{result.total === 1 ? '' : 's'}</span>
                <span className={styles.attached}>{result.attachedCount} attached</span>
                <span className={styles.missing}>{result.missingCount} not attached</span>
              </div>
              {seats.length ? (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Section</th>
                        <th>Row</th>
                        <th>Seat</th>
                        <th>Barcode</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {seats.map((seat) => (
                        <tr key={seat.tpId}>
                          <td>{seat.section || '—'}</td>
                          <td>{seat.row || '—'}</td>
                          <td>{seat.seat ?? '—'}</td>
                          <td className={seat.barcodeAttached ? styles.attached : styles.missing}>
                            {seat.barcode || 'Not attached'}
                          </td>
                          <td>{seat.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className={styles.muted}>No seats found for this listing.</p>
              )}
            </>
          )}
        </div>
        <div className={styles.footer}>
          <button type="button" className={styles.closeAction} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckBarcodesModal;
